import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    IconButton,
    TextField,
    Button,
    Paper,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    Container,
    Tooltip, TableContainer
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Layout from "./Layout";
import { useToast } from "../contexts/ToastContext";
import ServiceService from "../services/ServiceService";
import { useTranslation } from 'react-i18next';
import styles from  "./css/ServiceInfo.module.css"
import Divider from "@mui/material/Divider";

const ServiceInfoPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [service, setService] = useState(null);
    const [saved, setSaved] = useState(false);
    const { showToast } = useToast();
    const { t } = useTranslation();

    const validateService = () => {
        if (!service.name.trim()) {
            showToast(t('serviceInfo.toasts.serviceNameRequired'), 'error');
            return false;
        }

        for (const sub of service.subServices) {
            if (!sub.name?.trim()) {
                showToast(t('serviceInfo.toasts.subserviceNameRequired'), 'error');
                return false;
            }
            if (!sub.unit?.trim()) {
                showToast(t('serviceInfo.toasts.unitRequiredAll'), 'error');
                return false;
            }
            if (
                sub.price === '' ||
                sub.price === null ||
                Number.isNaN(Number(sub.price)) ||
                Number(sub.price) < 0
            ) {
                showToast(t('serviceInfo.toasts.priceInvalid'), 'error');
                return false;
            }
        }
        return true;
    };

    useEffect(() => {
        ServiceService.getService(id)
            .then(data => {
                if (data) {
                    // Ensure subServices array exists
                    setService({ ...data, subServices: data.subServices ?? [] });
                } else {
                    showToast(t('serviceInfo.toasts.serviceNotFound'), 'error');
                    navigate('/services');
                }
            })
            .catch(() => {
                showToast(t('serviceInfo.toasts.errorFetchingService'), 'error');
                navigate('/services');
            });
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubServiceChange = (index, field, value) => {
        const updated = [...service.subServices];
        updated[index] = { ...updated[index], [field]: value };
        setService({ ...service, subServices: updated });
        setSaved(false);
    };

    const handleAddSubService = () => {
        const newRow = {
            id: null,                 // null so backend creates a new one
            tempId: `tmp-${Date.now()}`, // local key
            name: '',
            unit: 'hours',
            price: 0,
            used: false
        };
        setService(s => ({ ...s, subServices: [...(s.subServices ?? []), newRow] }));
        setSaved(false);
    };

    const isDeletable = (sub) => {
        // Prefer usageCount if present; otherwise use inUse
        if (typeof sub.used === 'boolean') return sub.used === false;
        // If unknown, default to deletable=false (safe)
        return false;
    };

    const handleRemoveSubService = (index) => {
        const confirmDelete = window.confirm(t('serviceInfo.confirm.deleteSubservice'));
        if (!confirmDelete) return;
        const updated = service.subServices.filter((_, i) => i !== index);
        setService({ ...service, subServices: updated });
        setSaved(false);
    };

    const handleSaveChanges = async () => {
        if (!validateService()) return;

        const serviceDTO = {
            id: service.id,
            name: service.name.trim(),
            subServices: (service.subServices ?? []).map(sub => ({
                id: Number.isInteger(sub.id) ? sub.id : null,
                name: sub.name.trim(),
                unit: sub.unit.trim(),
                price: parseFloat(sub.price),
            }))
        };

        try {
            const updated = await ServiceService.updateService(service.id, serviceDTO);
            if (updated) {
                setService({ ...updated, subServices: updated.subServices ?? [] });
                setSaved(true);
                localStorage.setItem("TOAST_MESSAGE", JSON.stringify({
                    message: t('serviceInfo.toasts.serviceUpdatedSuccess', { name: updated.name }),
                    type: "success"
                }));
                navigate(`/services`);
            } else {
                showToast(t('serviceInfo.toasts.serviceNotFound'), 'error');
            }
        } catch (error) {
            showToast(t('serviceInfo.toasts.errorUpdatingService'), 'error');
            console.error('Update service error:', error);
        }
    };

    const handleDeleteService = async () => {
        if (!service) return;
        try {
            const success = await ServiceService.deleteService(service.id);
            if (success) {
                showToast(t('serviceInfo.toasts.serviceDeletedSuccess'), 'warning');
                navigate('/services');
            } else {
                showToast(t('serviceInfo.toasts.serviceNotFound'), 'error');
            }
        } catch (error) {
            // keep backend message as-is (might already be localized)
            showToast(error?.response?.data?.message || t('serviceInfo.toasts.errorUpdatingService'), 'error');
        }
    };

    if (!service) return <Typography>{t('serviceInfo.common.loading')}</Typography>;

    return (
        <Layout>
            <Container maxWidth="lg" className={styles.container}>
                <Box className={styles.pagePad}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(-1)}
                        className={styles.backBtn}
                    >
                        {t("serviceInfo.buttons.back")}
                    </Button>

                    <Paper elevation={3} className={styles.card}>
                        {/* Top toolbar */}
                        <Box className={styles.toolbar}>
                            <Typography variant="h5" className={styles.title}>
                                {t("serviceInfo.titles.manageService")}
                            </Typography>

                            <Button
                                startIcon={<AddIcon />}
                                variant="outlined"
                                onClick={handleAddSubService}
                                className={styles.addBtn}
                            >
                                {t("serviceInfo.buttons.addSubservice")}
                            </Button>
                        </Box>

                        {/* Service name */}
                        <TextField
                            label={t("serviceInfo.labels.serviceName")}
                            fullWidth
                            value={service.name}
                            onChange={(e) => {
                                setService({ ...service, name: e.target.value });
                                setSaved(false);
                            }}
                            className={styles.serviceName}
                        />

                        <Typography variant="h6" gutterBottom>
                            {t("serviceInfo.titles.subServices")}
                        </Typography>

                        {/* Table */}
                        <TableContainer className={styles.tableWrap}>
                            <Table size="small" className={styles.tableMin}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell className={styles.thCell}>
                                            {t("serviceInfo.labels.name")}
                                        </TableCell>
                                        <TableCell className={styles.thCell}>
                                            {t("serviceInfo.labels.unit")}
                                        </TableCell>
                                        <TableCell className={styles.thCell}>
                                            {t("serviceInfo.labels.priceChf")}
                                        </TableCell>
                                        <TableCell className={styles.thCell}>
                                            {t("serviceInfo.labels.actions")}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {(service.subServices ?? []).map((sub, index) => {
                                        const deletable = isDeletable(sub);
                                        return (
                                            <TableRow key={sub.id ?? sub.tempId ?? `row-${index}`}>
                                                <TableCell className={styles.tdCell}>
                                                    <TextField
                                                        disabled={!deletable}
                                                        size="small"
                                                        value={sub.name}
                                                        onChange={(e) =>
                                                            handleSubServiceChange(index, "name", e.target.value)
                                                        }
                                                        fullWidth
                                                    />
                                                </TableCell>

                                                <TableCell className={styles.tdCell}>
                                                    <FormControl fullWidth size="small" disabled={!deletable}>
                                                        <InputLabel>{t("serviceInfo.labels.unit")}</InputLabel>
                                                        <Select
                                                            label={t("serviceInfo.labels.unit")}
                                                            value={sub.unit}
                                                            onChange={(e) =>
                                                                handleSubServiceChange(index, "unit", e.target.value)
                                                            }
                                                        >
                                                            <MenuItem value="hours">{t("serviceInfo.units.hours")}</MenuItem>
                                                            <MenuItem value="pieces">{t("serviceInfo.units.pieces")}</MenuItem>
                                                            <MenuItem value="m">{t("serviceInfo.units.m")}</MenuItem>
                                                            <MenuItem value="m2">{t("serviceInfo.units.m2")}</MenuItem>
                                                            <MenuItem value="kg">{t("serviceInfo.units.kg")}</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </TableCell>

                                                <TableCell className={styles.tdCell}>
                                                    <TextField
                                                        disabled={!deletable}
                                                        type="number"
                                                        size="small"
                                                        value={sub.price}
                                                        onChange={(e) =>
                                                            handleSubServiceChange(index, "price", e.target.value)
                                                        }
                                                        inputProps={{ min: 0, step: "0.01" }}
                                                        fullWidth
                                                    />
                                                </TableCell>

                                                <TableCell className={`${styles.tdCell} ${styles.actionsCell}`}>
                                                    {deletable ? (
                                                        <IconButton onClick={() => handleRemoveSubService(index)}>
                                                            <DeleteIcon color="error" />
                                                        </IconButton>
                                                    ) : (
                                                        <Tooltip title={t("serviceInfo.tooltips.deleteDisabled")} arrow>
                              <span className={styles.inUseBadge}>
                                <InfoOutlinedIcon fontSize="small" className={styles.inUseIcon} />
                                  {t("serviceInfo.labels.inUseCannotDelete")}
                              </span>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Footer actions */}
                        <Divider className={styles.footerDivider} />
                        <Box className={styles.footerActions}>
                            <Button
                                variant="contained"
                                onClick={handleSaveChanges}
                                className={styles.primaryBtn}
                            >
                                {t("serviceInfo.buttons.saveChanges")}
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={handleDeleteService}
                                className={styles.deleteBtn}
                            >
                                {t("serviceInfo.buttons.deleteService")}
                            </Button>
                        </Box>
                    </Paper>
                </Box>
            </Container>
        </Layout>
    );
};

export default ServiceInfoPage;
